import {
  MdAccountBox,
  MdAdd,
  MdCheck,
  MdDelete,
  MdEdit,
  MdManageAccounts,
  MdMoreHoriz,
  MdOutlineEditNotifications,
  MdOutlineFingerprint,
  MdOutlineHideImage,
  MdOutlineImage,
  MdOutlineNavigateBefore,
  MdOutlineOpenInNew,
  MdOutlinePassword,
  MdSettings,
} from "react-icons/md";
import PageTitle from "../../components/PageTitle/PageTitle";
import TwoSidebars from "../../layouts/TwoSidebars/TwoSidebars";
import ButtonGroup from "../../components/ButtonGroup/ButtonGroup";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import useMobile from "../../hooks/useMobile";

import TextInput from "../../components/TextInput/TextInput";
import { useTranslation } from "react-i18next";
import { useEffect, useId, useMemo, useState } from "react";
import apiClient from "../../helpers/api-client";
import {
  API_ENDPOINT_UPDATE_PROFILE,
  APP_LOCAL_STORAGE_ACCOUNTS,
  BUTTON_STYLES,
  FILE_STORAGE_SERVER_URL,
  ICON_SIZES,
} from "../../helpers/constants";
import {
  COUNTRY_SELECT_OPTIONS,
  getAllTimezones,
  getShortName,
  objectHasChanges,
  showNotification,
} from "../../helpers/utils";
import SettingsBlocks from "../../components/SettingsBlocks/SettingsBlocks";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import { QRCodeSVG } from "qrcode.react";
import useCurrentUser from "../../hooks/useCurrentUser";
import useGeneralOptionsDropdownMenu from "../../hooks/useGeneralOptionsDropdownMenu";
import CustomSelect from "src/components/Select/CustomSelect";
import i18n from "../../i18n";
import CollapsibleBlock from "src/components/CollapsibleBlock/CollapsibleBlock";
import RadioGroup from "src/components/RadioGroup/RadioGroup";
import useModalsStore from "src/store/modalsStore";
import TextAreaInput from "src/components/TextAreaInput/TextAreaInput";
import CurrentUserPrivacyModal from "src/components/CurrentUserPrivacyModal/CurrentUserPrivacyModal";
import InfoBlock from "src/components/InfoBlock/InfoBlock";
import SourceAddContactDetailsModal from "../Sources/SourceAddContactDetailsModal";
import SourcesContactCards from "../Sources/SourcesContactCards";
import NetworkProfile from "src/components/NetworkProfile/NetworkProfile";
import { merge } from "lodash";
import NotificationSettings from "src/components/NotificationSettings/NotificationSettings";
import ChangesPendingToSave from "src/components/ChangesPendingToSave/ChangesPendingToSave";
import { EButtonKind } from "src/components/Button/ButtonTypes";

const FORM_ID = "update-profile";

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] =
    useState(false);

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
  };
  const { setShowCreatePassKey, setShowChangePassword } = useModalsStore();
  const [previewProfile, setPreviewProfile] = useState<boolean>(false);

  const currentLanguage = i18n.language?.toLowerCase?.() || "en";
  const { userInfo } = useCurrentUser();
  const { isMobile } = useMobile();
  const [country, setCountry] = useState<string>(userInfo?.country || "");
  const [name, setName] = useState<string>(userInfo?.name || "");
  const [showAddContactDetailsModal, setShowAddContactDetailsModal] =
    useState<boolean>(false);
  const [picture, setPicture] = useState<string>(
    userInfo?.picture
      ? `${FILE_STORAGE_SERVER_URL}/profile-thumbnail-${userInfo?.picture}`
      : ""
  );
  const [savedContactData, setSavedContactData] = useState<any[]>(
    userInfo?.settings?.contact || []
  );
  const [newContactData, setNewContactData] = useState<any[]>([]);

  const [intro, setIntro] = useState<string>(userInfo?.intro || "");
  const [mobilePhone, setMobilePhone] = useState<string>(
    userInfo?.mobile || ""
  );
  const [email, setEmail] = useState<string>(userInfo?.email || "");
  const [timezone, setTimezone] = useState<string>(userInfo?.timezone || "");

  const allTimezones = useMemo(
    () => getAllTimezones()?.map?.((tz: string) => ({ value: tz, label: tz })),
    []
  );

  useEffect(() => {
    setName(userInfo?.name || "");
    setPicture(
      userInfo?.picture
        ? `${FILE_STORAGE_SERVER_URL}/profile-thumbnail-${userInfo?.picture}`
        : ""
    );
    setEmail(userInfo?.email || "");
    setMobilePhone(userInfo?.mobile || "");
    setIntro(userInfo?.intro || "");
    setCountry(userInfo?.country || "");
    setTimezone(userInfo?.timezone);
    setSavedContactData(userInfo?.settings?.contact || []);

    updateQuickList({
      name: userInfo?.name,
      email: userInfo?.email,
      picture: userInfo?.picture,
    });
  }, [userInfo]);

  const [isUpdatingProfile, setIsUpdatingProfile] = useState<boolean>(false);
  const [file, setFile] = useState<any>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const tooltipId = useId();
  const { t } = useTranslation();
  const { resetCache } = useCurrentUser();

  const generalOptionsDropdown = useGeneralOptionsDropdownMenu();

  const updateProfile = async () => {
    setIsUpdatingProfile(true);

    const formData = new FormData();

    if (file) {
      formData.append("file", file);
    }

    formData.append("name", name);
    formData.append("email", email);
    formData.append("country", country);
    formData.append("intro", intro);
    formData.append("mobile", mobilePhone);
    formData.append("timezone", timezone);
    formData.append(
      "settings",
      JSON.stringify({
        ...userInfo?.settings,
        contact: mixedContactData,
      })
    );

    return apiClient
      .put(API_ENDPOINT_UPDATE_PROFILE, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(async (res: any) => {
        setNewContactData([]);
        await resetCache();

        updateQuickList({
          name,
          email,
          picture: userInfo?.picture,
        });

        showNotification({
          kind: "success",
          message: "Perfil actualizado correctamente",
        });
        setShowConfirmDialog(false);
      })
      .catch((e) => {
        showNotification({
          message: "Error actualizando tu perfil",
          kind: "error",
        });
      })
      .finally(() => {
        setIsUpdatingProfile(false);
      });
  };

  const updateQuickList = (newValue: any) => {
    const accounts = localStorage.getItem(APP_LOCAL_STORAGE_ACCOUNTS) || "[]";

    const { email, picture, name } = newValue;
    const parsedAccounts = JSON.parse(accounts);

    const exist = parsedAccounts?.find?.(
      (account: any) => account.email === email
    );

    if (!exist) {
      localStorage.setItem(
        APP_LOCAL_STORAGE_ACCOUNTS,
        JSON.stringify([...parsedAccounts, newValue])
      );
    } else {
      const index = parsedAccounts.findIndex(
        (account: any) => account.email === email
      );
      if (index !== -1) {
        parsedAccounts[index].picture = picture;
        parsedAccounts[index].name = name;
        localStorage.setItem(
          APP_LOCAL_STORAGE_ACCOUNTS,
          JSON.stringify(parsedAccounts)
        );
      }
    }
  };

  const canUpdateProfile = useMemo(() => {
    return name && email && country && mobilePhone && timezone;
  }, [name, email, country, mobilePhone, timezone]);

  const mixedContactData = useMemo(() => {
    return [...savedContactData, ...newContactData];
  }, [savedContactData, newContactData]);

  const previewUserInfo = useMemo(() => {
    const preview = {
      country,
      intro,
      name,
      picture: userInfo?.picture,
      timezone,
      settings: {
        contact: mixedContactData,
      },
    };

    return merge({}, userInfo, preview);
  }, [userInfo, name, country, timezone, intro, mixedContactData]);

  const hasChanges = useMemo(() => {
    return objectHasChanges({
      original: {
        name: userInfo?.name,
        country: userInfo?.country,
        timezone: userInfo?.timezone,
        intro: userInfo?.intro,
        contact: userInfo?.settings?.contact || [],
      },
      edited: {
        name,
        country,
        timezone,
        intro,
        contact: mixedContactData,
      },
    });
  }, [userInfo, name, country, timezone, intro, mixedContactData]);

  return (
    <>
      <TwoSidebars
        right={
          isMobile ? null : (
            <SettingsBlocks layout="vertical" columns={isMobile ? 2 : 1} />
          )
        }
      >
        <div className={clsx(["flex", "w-full", "flex-col", "gap-0"])}>
          <PageTitle
            titleLink="/settings"
            icon={<MdSettings size={ICON_SIZES.MD} />}
            title={t("settings.title")}
            subtitle={t("profile_page.title")}
            isLoading={false}
            rightSide={
              <>
                <ButtonGroup
                  align="right"
                  gap={1}
                  buttons={[
                    {
                      icon: <MdMoreHoriz size={ICON_SIZES.MD} />,
                      tooltip: t("Más opciones"),
                      className: isMobile ? BUTTON_STYLES.HEADING : "",
                      options: [
                        {
                          onClick: () => setShowChangePassword(true),
                          icon: <MdOutlinePassword size={ICON_SIZES.SM} />,
                          text: "Cambiar contraseña",
                          className: isMobile ? BUTTON_STYLES.HEADING : "",
                        },
                        {
                          onClick: () => setShowCreatePassKey(true),
                          icon: <MdOutlineFingerprint size={ICON_SIZES.SM} />,
                          text: "Acceso sin contraseña",
                          className: isMobile ? BUTTON_STYLES.HEADING : "",
                        },
                        {
                          onClick: () => setShowNotificationSettings(true),
                          icon: (
                            <MdOutlineEditNotifications size={ICON_SIZES.SM} />
                          ),
                          text: "Notificaciones",
                          className: isMobile ? BUTTON_STYLES.HEADING : "",
                        },

                        {
                          onClick: () => setShowPrivacyModal(true),
                          icon: <MdManageAccounts size={ICON_SIZES.SM} />,
                          text: "Privacidad",
                          className: isMobile ? BUTTON_STYLES.HEADING : "",
                        },
                        {
                          onClick: () => {
                            const el = document?.getElementById?.(
                              "select-file-to-upload"
                            );

                            if (el) {
                              el?.click?.();
                            }
                          },
                          hide: false,
                          icon: <MdOutlineImage size={ICON_SIZES.SM} />,
                          text: "Seleccionar una imagen",
                          className: isMobile ? BUTTON_STYLES.HEADING : "",
                        },
                        {
                          onClick: () => {
                            setPicture("");
                            setFile("");
                          },
                          hide: !picture,
                          icon: <MdOutlineHideImage size={ICON_SIZES.SM} />,
                          text: "Eliminar la imagen",
                          className: isMobile ? BUTTON_STYLES.HEADING : "",
                        },
                      ],
                    },
                    {
                      icon: <MdOutlineNavigateBefore size={ICON_SIZES.SM} />,
                      tooltip: "Atras",
                      onClick: () => {
                        navigate("/settings");
                      },
                      className: isMobile ? BUTTON_STYLES.HEADING : "",
                    },
                    generalOptionsDropdown,
                  ]}
                />
              </>
            }
          />
          <div className="grid grid-cols-1 gap-4 md:gap-6">
            <input
              className="hidden"
              accept="image/*"
              type="file"
              id="select-file-to-upload"
              onChange={async (e: any) => {
                const attachment = e?.target?.files;

                if (attachment?.length) {
                  setFile(attachment?.[0]);
                  setPicture(URL.createObjectURL(attachment?.[0]));
                } else {
                  setFile(null);
                  setPicture("");
                }
              }}
              required
            />

            <div
              className={clsx([
                "grid md:flex md:justify-center",
                "grid-cols-2 md:flex-row",
                "gap-4 md:gap-6",
              ])}
            >
              <div
                className={clsx([
                  "flex",
                  "justify-start",
                  "items-center",
                  "!max-h-[120px]",
                  "!min-h-[120px]",
                  "md:!max-h-[240px]",
                  "md:!min-h-[240px]",
                  "md:w-[240px]",
                  "rounded-md md:rounded-lg",
                  "overflow-hidden",
                ])}
              >
                {picture ? (
                  <img
                    alt=""
                    className={clsx([
                      "w-full h-full",
                      "shadow-md",
                      "cursor-pointer",
                      "bg-checkers",
                      "object-cover",
                    ])}
                    data-tooltip-id={tooltipId}
                    data-tooltip-content="Insertar imagen"
                    data-tooltip-float
                    data-tooltip-position-strategy="fixed"
                    data-tooltip-offset={20}
                    src={picture}
                    onClick={() => {
                      const el = document?.getElementById?.(
                        "select-file-to-upload"
                      );

                      if (el) {
                        el?.click?.();
                      }
                    }}
                  />
                ) : (
                  <div
                    className={clsx([
                      "w-full",
                      "h-full",
                      "flex",
                      "items-center",
                      "justify-center",
                      "bg-checkers",
                    ])}
                  >
                    <MdAccountBox
                      onClick={() => {
                        const el = document?.getElementById?.(
                          "select-file-to-upload"
                        );

                        if (el) {
                          el?.click?.();
                        }
                      }}
                      size={isMobile ? ICON_SIZES.XL * 1.5 : ICON_SIZES.XL * 2}
                      className="text-gray-600"
                    />
                  </div>
                )}
              </div>
              <div
                className={clsx([
                  "flex",
                  "justify-center",
                  "flex-col",
                  "gap-2",
                  "items-center",
                  "!max-h-[120px]",
                  "!min-h-[120px]",
                  "md:!max-h-[240px]",
                  "md:!min-h-[240px]",
                  "md:w-[240px]",
                  "rounded-md md:rounded-lg",
                  "overflow-hidden",
                  "bg-checkers",
                ])}
              >
                <QRCodeSVG
                  value={[
                    "BEGIN:VCARD",
                    "VERSION:2.1",
                    `N:${name};`,
                    `FN:${name}`,
                    `EMAIL:${email}`,
                    `TEL;TYPE=cell:${mobilePhone}`,
                    "END:VCARD",
                  ].join("\r\n")}
                  title={"eXpenser.net"}
                  fgColor={"#212f3c"}
                  height={isMobile ? ICON_SIZES.XL * 1.5 : ICON_SIZES.XL * 2.5}
                  level={"L"}
                  type=""
                  width={isMobile ? ICON_SIZES.XL * 1.5 : ICON_SIZES.XL * 2.5}
                  boostLevel
                  marginSize={0}
                />
              </div>
            </div>

            <CollapsibleBlock title="Idioma">
              <div className={clsx(["flex", "flex-col", "gap-4", "w-full"])}>
                <div
                  className={clsx([
                    "flex",
                    "w-full",
                    "text-sm",
                    "md:text-base",
                  ])}
                >
                  Selecciona tu idioma de preferencia para la aplicación.
                </div>
                <RadioGroup
                  onChange={(lang: any) => {
                    changeLanguage(lang);
                  }}
                  selected={currentLanguage?.startsWith?.("es") ? "es" : "en"}
                  options={[
                    {
                      value: "es",
                      label: "Español",
                      description: "Mostrar en Español",
                    },
                    {
                      value: "en",
                      label: "English",
                      description: "Mostrar en Inglés",
                    },
                  ]}
                />
              </div>
            </CollapsibleBlock>

            <CollapsibleBlock title="Información personal">
              <div
                className={clsx([
                  "flex",
                  "flex-col",
                  "w-full",
                  "gap-4",
                  // "bg-red-400",
                ])}
              >
                <div
                  className={clsx([
                    "grid",
                    "grid-cols-1",
                    "md:grid-cols-1",
                    "gap-4",
                  ])}
                >
                  <div
                    className={clsx([
                      "grid",
                      "grid-cols-1",
                      "md:grid-cols-2",
                      "gap-4",
                      "md:gap-4",
                    ])}
                  >
                    <TextInput
                      value={name}
                      onChange={(e: any) => setName(e?.target?.value)}
                      label={t("profile_page.name_label")}
                      placeholder={t("profile_page.name_placeholder")}
                      required
                      autoComplete="name nickname"
                    />
                    <TextInput
                      value={mobilePhone}
                      onChange={(e: any) => setMobilePhone(e?.target?.value)}
                      label={t("Teléfono móvil")}
                      placeholder={t("Digita tu número de teléfono móvil")}
                      autoComplete="tel tel-country-code"
                      type="tel"
                    />
                    <CustomSelect
                      options={COUNTRY_SELECT_OPTIONS}
                      value={country}
                      onChange={(e: any) => setCountry(e?.value)}
                      label={t("profile_page.country_label")}
                      useValueAsLabel={false}
                      placeholder="Selecciona tu país"
                      required
                      allowSearchModal
                    />
                    <CustomSelect
                      options={allTimezones}
                      value={timezone}
                      onChange={(e: any) => setTimezone(e?.value)}
                      label={t("Zona horaria")}
                      useValueAsLabel={false}
                      placeholder="Selecciona tu zona horaria"
                      required
                      allowSearchModal
                    />
                  </div>
                  <TextInput
                    value={email}
                    onChange={(e: any) => setEmail(e?.target?.value)}
                    label={t("Correo electrónico")}
                    type="email"
                    placeholder={t("Digita tu correo electrónico")}
                    autoComplete="email"
                  />
                  <TextAreaInput
                    value={intro}
                    onChange={(e) => setIntro(e?.target?.value)}
                    placeholder={
                      "Puedes ingresar una descripción breve de quién eres o lo qué haces. Tus contactos podrán ver la información en tu perfil."
                    }
                    label="Acerca de mí"
                  />
                </div>
              </div>
            </CollapsibleBlock>

            <CollapsibleBlock title="Datos de contacto">
              <div
                className={clsx([
                  "flex",
                  "w-full",
                  "flex-col",
                  "gap-4",
                  "relative",
                ])}
              >
                <InfoBlock
                  message="Agrega datos de contacto a tu perfil."
                  displayMobileIcon
                  buttons={[
                    {
                      text: isMobile ? "" : "Agregar",
                      icon: <MdAdd size={ICON_SIZES.MD} />,
                      onClick: () => setShowAddContactDetailsModal(true),
                    },
                  ]}
                />
                {mixedContactData?.length ? (
                  <SourcesContactCards
                    items={mixedContactData}
                    columns={2}
                    actions={[
                      {
                        icon: <MdDelete size={ICON_SIZES.MD} />,
                        kind: EButtonKind.DANGER,
                        onClick: () => {},
                      },
                      {
                        icon: <MdEdit size={ICON_SIZES.MD} />,
                        kind: EButtonKind.EDIT,
                        onClick: () => {},
                      },
                    ]}
                  />
                ) : null}
              </div>
            </CollapsibleBlock>

            <div
              className={clsx(["flex", "gap-4", "flex-row", "justify-between"])}
            >
              <ChangesPendingToSave show={hasChanges} />
              <div className={clsx(["flex", "w-full", "justify-end"])}>
                <ButtonGroup
                  buttons={[
                    {
                      tooltip: t("Ver"),
                      onClick: () => {
                        setPreviewProfile(true);
                      },
                      icon: <MdOutlineOpenInNew size={32} />,
                      kind: EButtonKind.NORMAL,
                      disabled: isUpdatingProfile,
                      hide: true,
                    },
                    {
                      text: t("shared.update"),
                      onClick: () => {
                        setShowConfirmDialog(true);
                      },
                      icon: <MdCheck size={32} />,
                      kind: EButtonKind.SAVE,
                      isLoading: isUpdatingProfile,
                      disabled:
                        isUpdatingProfile || !canUpdateProfile || !hasChanges,
                    },
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
      </TwoSidebars>
      {showConfirmDialog && (
        <ConfirmDialog
          title={t("Actualizar")}
          description={t("dialogs.profile.update_dialog_question")}
          onClose={() => {
            setShowConfirmDialog(false);
          }}
          confirmKind={EButtonKind.SAVE}
          onConfirm={async () => {
            await updateProfile();
          }}
          isLoading={isUpdatingProfile}
        />
      )}

      {showPrivacyModal && (
        <CurrentUserPrivacyModal onClose={() => setShowPrivacyModal(false)} />
      )}

      {showAddContactDetailsModal && (
        <SourceAddContactDetailsModal
          onClose={() => setShowAddContactDetailsModal(false)}
          onConfirm={(data: any) => {
            setNewContactData((prev: any) => [...prev, data]);
            setShowAddContactDetailsModal(false);
          }}
        />
      )}

      {previewProfile && (
        <NetworkProfile
          onClose={() => setPreviewProfile(false)}
          actions={[]}
          title={`Acerca de ${getShortName(name)}`}
          profileInfo={previewUserInfo}
        />
      )}

      {showNotificationSettings && (
        <NotificationSettings
          showSubscriptionMessage
          isModal
          onModalClose={() => setShowNotificationSettings(false)}
        />
      )}
    </>
  );
};

export default ProfilePage;
